// szczegóły produktu

import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { ChevronDown, X, Check2 } from 'react-bootstrap-icons';
import Tooltip from '@mui/material/Tooltip';
import { Trans } from 'react-i18next';

import { useAppNavigate } from 'hooks';
import { IProduct, ITechnicalAttribute, IVariant } from 'api/types';
import { Link, Modal, Button } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/pages/Product/components/Details/Details.module.scss';

// typ danych wejściowych
interface IProps {
  product: IProduct;
  scrollToDescription: () => void;
  h1?: string | null;
}

const Details: FC<IProps> = ({ product, scrollToDescription, h1 }) => {
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  // pokazywanie popupa z GPSR
  const [technicalInformationVisible, setTechnicalInformationVisible] = useState(false);

  // renderowanie atrybutu
  const renderTechnicalAttributeValue = (item: ITechnicalAttribute) =>
    item.type === 'boolean' ? (
      item.value ? (
        <Check2 className={styles.green} />
      ) : (
        <X className={styles.red} />
      )
    ) : (
      item.value
    );

  const renderGpsrContent = () => {
    const gpsrData = product.gpsr_supplier_info;
    return (
      <div>
        <table>
          <tr>
            <th />
            <th />
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>Nazwa</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>{gpsrData?.registered_trade_name}</td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>NIP</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>{gpsrData?.tax_number}</td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>Email</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>
              <a href={`mailto:${gpsrData?.email}`}>{gpsrData?.email}</a>
            </td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>Telefon</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>{gpsrData?.phone}</td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={classnames(styles.gpsrDataCol, styles.gpsrAddress)}>
              <div>
                <Trans>Dane adresowe</Trans>:
              </div>
            </td>
            <td className={styles.gpsrDataCol}>
              <div>
                {gpsrData?.address?.street} {gpsrData?.address?.house_number}
                {gpsrData?.address?.apartment_number && `/${gpsrData?.address?.apartment_number}`}
                <br />
                {gpsrData?.address?.postal_code}, {gpsrData?.address?.city}{' '}
                {gpsrData?.address?.country}
              </div>
            </td>
          </tr>
        </table>
      </div>
    );
  };

  return (
    <div
      className={classnames(styles.productDetails, 'StylePath-Pages-Product-components-Details')}>
      <div className={styles.mainContent}>
        {/* Po co: Używamy H1 z SEO jeśli jest dostępne, w przeciwnym razie używamy tytułu produktu */}
        <h1 itemProp="name" className={styles.title}>
          {h1 || product?.product_basic_full_info?.title}
        </h1>

        <ul className={styles.list}>
          <li className={styles.listItem}>
            <Trans>INDEX</Trans>: <strong>{product?.product_basic_full_info?.index}</strong>
          </li>
          {product?.product_basic_full_info?.producer_name && (
            <li
              className={styles.listItem}
              itemScope
              itemProp="brand"
              itemType="http://schema.org/Brand">
              <Trans>Producent</Trans>:{' '}
              <strong itemProp="name">{product?.product_basic_full_info?.producer_name}</strong>
            </li>
          )}
          <li className={styles.listItem}>
            <Trans>Kategoria</Trans>:{' '}
            <strong>
              <Link to={`/${product?.product_basic_full_info?.category_url}`}>
                {product?.product_basic_full_info?.category_name}
              </Link>
            </strong>
          </li>
        </ul>

        <div className={styles.description}>
          <span className={styles.label}>
            <Trans>Opis</Trans>
          </span>
          <div itemProp="description" className={styles.text}>
            {product.product_basic_full_info?.short_description}
          </div>
        </div>

        <div className={styles.technicalAttributes}>
          <div className={styles.block}>
            {product?.product_basic_full_info?.technical_attributes
              ?.filter((item) => item.highlighted)
              .map((item) => (
                <div className={styles.technicalAttributeItem} key={item.name}>
                  {item.name}{' '}
                  <span className={styles.value}>{renderTechnicalAttributeValue(item)}</span>
                </div>
              ))}
          </div>

          <button onClick={scrollToDescription}>
            <Trans>Przewiń do pełnego opisu</Trans> <ChevronDown />
          </button>

          {!!product.gpsr_supplier_info && (
            <button
              className={classnames(styles.sizingTable, styles.gpsrButton)}
              onClick={() => setTechnicalInformationVisible(true)}>
              <Trans>Informacja o podmiocie wprowadzającym na rynek</Trans>
            </button>
          )}
        </div>

        {((product.product_variants_full_info?.variants &&
          product.product_variants_full_info?.variants?.length) ||
          (product?.product_colors_full_info?.colors &&
            product?.product_colors_full_info?.colors.length > 0)) && (
          <div className={styles.options}>
            <div className={styles.optionsTitle}>
              <Trans>Dostępne rozmiary i kolory</Trans>:
            </div>

            {product.product_variants_full_info?.variants &&
              product.product_variants_full_info?.variants?.length > 0 && (
                <div className={styles.versionsWrapper}>
                  <WiseSelect<IVariant>
                    onChange={(item) => {
                      // Type guard - sprawdzamy czy to nie jest tablica (multichoice)
                      if (!Array.isArray(item) && item) {
                        navigate(
                          item.payloads_list.variant_basic_full_info.url_link
                            ? `/${item.payloads_list.variant_basic_full_info.url_link}`
                            : `/products/${item.id}`
                        );
                      }
                    }}
                    options={
                      product.product_variants_full_info?.variants &&
                      product.product_variants_full_info?.variants?.map((version) => ({
                        value: version.id,
                        label: version.payloads_list.variant_basic_full_info.title,
                        item: version
                      }))
                    }
                    value={product.id}
                  />
                </div>
              )}

            {product?.product_colors_full_info?.colors &&
              product?.product_colors_full_info?.colors.length > 0 && (
                <div className={styles.colorsWrapper}>
                  {product?.product_colors_full_info?.colors.map((color) => (
                    <Tooltip
                      title={color.payloads_list.color_variant_basic_full_info.title}
                      key={color.id}>
                      <span
                        className={classnames(styles.color, {
                          [styles.current]: color.id === product.id
                        })}
                        style={{
                          background: color.payloads_list.color_variant_basic_full_info.color
                        }}
                        onClick={() =>
                          navigate(
                            color.payloads_list.color_variant_basic_full_info.url_link
                              ? `/${color.payloads_list.color_variant_basic_full_info.url_link}`
                              : `/products/${color.id}`
                          )
                        }
                      />
                    </Tooltip>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>

      {technicalInformationVisible && (
        <Modal
          title={t('Informacja o dostawcy')}
          onClose={() => {
            setTechnicalInformationVisible(false);
          }}>
          {renderGpsrContent()}
          <div className={styles.confirmationModalActions}>
            <Button onClick={() => setTechnicalInformationVisible(false)}>
              <Trans>Zamknij</Trans>
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Details;
